import { useIonModal } from '@ionic/react'
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces'

import { useGetUnitGenericDetails, useUpdateUnitTechCompany } from '@apps-common/apis/portal/unit'
import { AccountChangeModal } from '@apps-common/apps/portal/pages/units/modals/AccountChangeModal'
import { DetailsItem, DetailsItemAction } from '@apps-common/apps/portal/pages/units/pages/manage/components/details'
import { CardContainer, ImageComponent } from '@apps-common/components/common'
import { SectionTitle } from '@apps-common/components/common/portal-title'
import { VertSpacerLg } from '@apps-common/components/layout'
import { CompanySelectModal } from '@apps-common/components/modals'
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { SiteDetailsModal } from '@apps-common/components/modals/site/SiteDetailsModal'
import { QRCodePortalUnitButton } from '@apps-common/components/qr-code/qr-code-portal-unit'
import { AppType } from '@apps-common/config/app-types'
import { CompanyType } from '@apps-common/config/company'
import { ModalDismiss } from '@apps-common/config/modal'
import { invalidateAllUnitQueries } from '@apps-common/helpers/invalidate-unit-queries'
import { unitIsActive, unitIsCancelled } from '@apps-common/helpers/unit-helpers'
import { valueIsValidCheck, valueStringCheck } from '@apps-common/helpers/value-string-check'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import moment from 'moment'

interface IPortalUnitDetailsModalProps {
  status: string
  unitId: string
}

export const PortalUnitDetailsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IPortalUnitDetailsModalProps
}) => {
  const { status, unitId } = componentProps || {}
  const { data: unit, isLoading: unitIsLoading, isError: unitIsError, refetch } = useGetUnitGenericDetails({ status, unitId })

  const [presentSiteDetails, dismissSiteDetails] = useIonModal(SiteDetailsModal, {
    componentProps: { unitId },
    onDismiss: (data: string, role: string) => dismissSiteDetails(data, role),
    enableBackdropDismiss: false
  })

  const [presentTechChange, dismissTechChange] = useIonModal(CompanySelectModal, {
    componentProps: {
      subtitle: 'Update',
      title: 'Technician',
      companyType: CompanyType.Technician,
      noResultsDescription: 'Search above for technician companies'
    },
    onDismiss: (data: string, role: string) => dismissTechChange(data, role),
    enableBackdropDismiss: false
  })

  const [presentAccountChange, dismissAccountChange] = useIonModal(AccountChangeModal, {
    componentProps: { unitId, caller: 'portal' },
    onDismiss: (data: string, role: string) => {
      if (role === ModalDismiss.Confirm) {
        refetch()
      }
      dismissAccountChange(data, role)
    }
  })

  const { mutate: updateTechCompany, isPending: isUpdatingTechCompany } = useUpdateUnitTechCompany(true, () => {
    invalidateAllUnitQueries(unit?.unitId || '', AppType.portal)
  })

  const isActive = unitIsActive(unit)
  // const isInactive = unitIsInactive(unit)
  const isCancelled = unitIsCancelled(unit)
  const loading = unitIsLoading || unitIsError

  console.log(isActive, isCancelled)

  return (
    <ModalContainer innerClasses='' onDismiss={onDismiss}>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader title='Device Details' />
          <div className='flex items-center justify-end gap-[10px]'>
            <QRCodePortalUnitButton key='portal-unit-qr-code-generate-icon' disabled={isCancelled} unitId={unitId} />
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[70px] !pb-[50px] sm:!pb-[25px]'>
        <div className='grid gap-[20px] max-w-[1200px]'>
          <CardContainer bgClasses='bg-surface border border-border text-text'>
            <SectionTitle text='Company' classes='ml-[10px]' />
            <VertSpacerLg />
            <div className='grid lg:grid-cols-2 gap-[15px] px-[10px]'>
              <DetailsItemAction
                label='Site'
                value={valueStringCheck(unit?.siteName)}
                allowCopy={!!unit?.siteName}
                callback={() =>
                  presentSiteDetails({
                    enterAnimation: modalSlideUpEnterAnimation,
                    leaveAnimation: modalSlideUpLeaveAnimation,
                    backdropDismiss: false,
                    onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                      if (ev.detail.role === ModalDismiss.Confirm) {
                        invalidateAllUnitQueries(unitId, AppType.portal)
                      }
                    }
                  })
                }
                loading={loading}
              />
              <DetailsItemAction
                loading={loading}
                label='Monitoring Centre'
                value={valueStringCheck(unit?.mCentre)}
                allowCopy={valueIsValidCheck(unit?.mCentre)}
              />
              <DetailsItemAction
                loading={loading}
                label='Bureau'
                value={valueStringCheck(unit?.bureau)}
                allowCopy={valueIsValidCheck(unit?.bureau)}
              />
              <DetailsItemAction
                loading={loading || isUpdatingTechCompany}
                label='Technician'
                value={valueStringCheck(unit?.techCompany)}
                allowCopy={valueIsValidCheck(unit?.techCompany)}
                callback={() =>
                  presentTechChange({
                    enterAnimation: modalSlideUpEnterAnimation,
                    leaveAnimation: modalSlideUpLeaveAnimation,
                    backdropDismiss: false,
                    onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                      if (ev.detail.role === ModalDismiss.Confirm) {
                        updateTechCompany(ev?.detail?.data?.companyId)
                      }
                    }
                  })
                }
              />
            </div>
          </CardContainer>
          <CardContainer bgClasses='bg-surface border border-border text-text'>
            <SectionTitle text='Device' classes='ml-[10px]' />
            <VertSpacerLg />
            <div className='grid lg:grid-cols-2 gap-[20px] px-[10px]'>
              <div className='flex relative left-[-10px]'>
                {loading && (
                  <div className='bg-skeleton rounded-full flex items-center justify-start gap-[10px] p-[5px] pr-[15px] animate-pulse'>
                    <div className='bg-skeleton-hi p-[10px] rounded-full w-[50px] h-[50px]' />
                    <div className='grow flex flex-col gap-[3px]'>
                      <div className='bg-skeleton-hi w-[45px] h-[14px] rounded' />
                      <div className='bg-skeleton-hi w-[65px] h-[18px] rounded' />
                    </div>
                  </div>
                )}
                {!loading && (
                  <div className='bg-surface-2 border border-border rounded-full flex items-center justify-start gap-[10px] p-[5px] pr-[15px]'>
                    <ImageComponent
                      src={unit?.imageUrl}
                      alt={unit?.product || 'Product name'}
                      containerClasses='bg-skeleton-hi border border-border p-[10px] rounded-full w-[50px] h-[50px]'
                      imageClasses='w-[32px] h-[32px]'
                    />
                    <div>
                      <div className='text-text text-[0.8em] jost'>Product</div>
                      <div className='text-text text-[0.9em] jost-light'>{unit?.product}</div>
                    </div>
                  </div>
                )}
              </div>
              <DetailsItemAction loading={loading} label='Plan' value={valueStringCheck(unit?.planName)} />
              {isActive && (
                <>
                  <DetailsItem classes='hidden sm:block' label='Monthly plan' value={unit?.planName} loading={loading} />
                  <DetailsItem
                    classes='hidden sm:block'
                    label='Activated'
                    value={moment(unit?.dateLoaded).format('DD MMM YY')}
                    loading={loading}
                  />
                </>
              )}
              {isCancelled && (
                <>
                  <DetailsItem classes='hidden sm:block' label='Monthly plan' value={unit?.planName} loading={loading} />
                  <DetailsItem
                    classes='hidden sm:block'
                    label='Cancelled on'
                    value={moment(unit?.dateDeleted).format('DD MMM YY')}
                    loading={loading}
                  />
                </>
              )}
              <DetailsItemAction
                loading={loading}
                label='CID Account'
                value={valueStringCheck(unit?.accountNo)}
                callback={() => presentAccountChange()}
                allowCopy={valueIsValidCheck(unit?.accountNo)}
              />
              <DetailsItemAction loading={loading} label='Network' value={valueStringCheck(unit?.networkType)} />
              <DetailsItemAction
                loading={loading}
                label='Mobile No'
                value={valueStringCheck(unit?.mobileNo)}
                allowCopy={valueIsValidCheck(unit?.mobileNo)}
              />
              <DetailsItemAction
                loading={loading}
                label='Sim No'
                value={valueStringCheck(unit?.simNo)}
                allowCopy={valueIsValidCheck(unit?.simNo)}
              />
              <DetailsItemAction
                loading={loading}
                label='IMEI'
                value={valueStringCheck(unit?.imei)}
                allowCopy={valueIsValidCheck(unit?.imei)}
              />
            </div>
          </CardContainer>
        </div>
      </ModalBody>
    </ModalContainer>
  )
}
